// About Us — section with 8 sub-pages including the expanded Lead Consultant profile.
// Strict adherence to the supplied content — nothing is summarised.
import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ChevronRight, Mail, Phone, MapPin, CheckCircle2, Globe2, Award,
  Briefcase, GraduationCap, Building, BookOpen, Sparkles, Target,
  Users, Shield, Activity, TrendingUp,
} from 'lucide-react';
import PublicSiteShell from '@/components/layout/PublicSiteShell';
import { CORPORATE_BRAND, ABOUT_CONTENT, ABOUT_PAGES, CORPORATE_PILLARS } from '@/lib/publicSiteContent';
import { CORPORATE_MENU } from '@/pages/CorporateSite';

/* ================================================================== */
/*  ABOUT ROUTER                                                       */
/* ================================================================== */
export default function AboutRouter() {
  const { slug } = useParams();
  if (!slug) return <Navigate to="/about/about-attp" replace />;
  const data = ABOUT_CONTENT[slug];
  if (!data) return <Navigate to="/about/about-attp" replace />;

  const b = CORPORATE_BRAND;

  return (
    <PublicSiteShell brand={b} menu={CORPORATE_MENU} chatContext={`Visitor on About — ${data.title}.`}>
      <SubpageHero brand={b} title={data.title} intro={data.intro} activeSlug={slug} />
      <div className="bg-white">
        <div className="container-x py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28 space-y-1.5">
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>About Us</div>
              {ABOUT_PAGES.map((p) => (
                <Link
                  key={p.slug}
                  to={`/about/${p.slug}`}
                  className="block px-4 py-2.5 text-sm rounded-md transition-colors border-l-2"
                  style={{
                    background: slug === p.slug ? `${b.accent}10` : 'transparent',
                    borderLeftColor: slug === p.slug ? b.accent : 'transparent',
                    color: slug === p.slug ? b.primary : '#525252',
                    fontWeight: slug === p.slug ? 600 : 500,
                  }}
                  data-testid={`about-nav-${p.slug}`}
                >
                  {p.label}
                </Link>
              ))}
            </div>
          </aside>

          <main className="lg:col-span-9 min-w-0">
            {slug === 'about-attp' && <AboutAttp data={data} brand={b} />}
            {slug === 'mission-vision' && <MissionVision data={data} brand={b} />}
            {slug === 'core-values' && <CoreValues data={data} brand={b} />}
            {slug === 'lead-consultant' && <LeadConsultant data={data} brand={b} />}
            {slug === 'countries-of-operation' && <Countries data={data} brand={b} />}
            {slug === 'why-choose-us' && <WhyChooseUs data={data} brand={b} />}
            {slug === 'our-approach' && <OurApproach data={data} brand={b} />}
            {slug === 'contact-us' && <ContactUs data={data} brand={b} />}
          </main>
        </div>
      </div>
    </PublicSiteShell>
  );
}

function SubpageHero({ brand: b, title, intro, activeSlug }) {
  return (
    <section className="relative overflow-hidden py-14 lg:py-20" style={{ background: `linear-gradient(135deg, ${b.primary} 0%, #2a2620 100%)`, color: '#FFFFFF' }} data-testid={`about-hero-${activeSlug}`}>
      <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full blur-[120px]" style={{ background: `${b.accent}30` }} />
      <div className="container-x relative">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-4 inline-flex items-center gap-2" style={{ color: b.accent }}>
          <Link to="/corporate" className="hover:underline">Corporate Hub</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span>About Us</span>
        </div>
        <h1 className="font-display font-bold tracking-tight text-4xl lg:text-5xl leading-[1.05] max-w-3xl">{title}</h1>
        {intro && <p className="mt-5 text-lg leading-relaxed max-w-3xl" style={{ color: 'rgba(255,255,255,0.78)' }}>{intro}</p>}
      </div>
    </section>
  );
}

/* ================================================================== */
/*  About ATTP                                                          */
/* ================================================================== */
function AboutAttp({ data, brand: b }) {
  return (
    <div className="space-y-8">
      {data.sections.map((s, i) => (
        <article key={s.heading} className="prose prose-neutral max-w-none">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>
            {String(i + 1).padStart(2, '0')} · Section
          </div>
          <h2 className="font-display font-bold text-2xl lg:text-3xl mb-4 leading-tight">{s.heading}</h2>
          <p className="text-base text-neutral-700 leading-relaxed">{s.body}</p>
        </article>
      ))}

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {CORPORATE_PILLARS.map((p) => {
          const Icon = p.slug === 'business-consulting' ? Briefcase : p.slug === 'corporate-training' ? Users : GraduationCap;
          return (
            <Link key={p.slug} to={`/corporate/${p.slug}`} className="group p-5 rounded-md border hover:-translate-y-0.5 hover:shadow-md transition-all" style={{ borderColor: '#E5E7EB' }}>
              <div className="h-11 w-11 rounded-md flex items-center justify-center mb-3" style={{ background: `${b.accent}15`, color: b.accent }}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="font-display font-semibold text-base mb-1">{p.title}</div>
              <p className="text-xs text-neutral-600 leading-relaxed">{p.short}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Mission & Vision                                                   */
/* ================================================================== */
function MissionVision({ data, brand: b }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {data.sections.map((s) => (
        <article key={s.heading} className="p-7 rounded-md border" style={{ borderColor: '#E5E7EB', background: s.heading === 'Our Vision' ? b.primary : '#FFFFFF', color: s.heading === 'Our Vision' ? '#FFFFFF' : undefined }}>
          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>{s.heading}</div>
          <p className={`text-base leading-relaxed ${s.heading === 'Our Vision' ? '' : 'text-neutral-700'}`} style={s.heading === 'Our Vision' ? { color: 'rgba(255,255,255,0.85)' } : {}}>{s.body}</p>
        </article>
      ))}
    </div>
  );
}

/* ================================================================== */
/*  Core Values                                                        */
/* ================================================================== */
function CoreValues({ data, brand: b }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {data.values.map((v) => (
        <div key={v.name} className="p-6 rounded-md border" style={{ borderColor: '#E5E7EB' }}>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5" style={{ color: b.accent }} />
            <h3 className="font-display font-semibold text-lg">{v.name}</h3>
          </div>
          <p className="text-sm text-neutral-700 leading-relaxed">{v.body}</p>
        </div>
      ))}
    </div>
  );
}

/* ================================================================== */
/*  Lead Consultant — heavily expanded profile                         */
/* ================================================================== */
function LeadConsultant({ data, brand: b }) {
  const blocks = [
    { key: 'philosophy', label: 'Leadership Philosophy', icon: Target },
    { key: 'methodology', label: 'Consulting Methodology', icon: Activity },
    { key: 'trainingExperience', label: 'Training & Facilitation Experience', icon: Users },
    { key: 'educationalLeadership', label: 'Educational Leadership Expertise', icon: BookOpen },
    { key: 'orgDevExpertise', label: 'Organisational Development Expertise', icon: Building },
    { key: 'digitalTransformation', label: 'Digital Transformation Expertise', icon: Sparkles },
    { key: 'strategicAdvisory', label: 'Strategic Advisory Experience', icon: TrendingUp },
    { key: 'panAfrican', label: 'Pan-African Experience', icon: Globe2 },
  ];

  return (
    <div className="space-y-10">
      {/* Header card */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-5">
          <div className="aspect-[4/5] rounded-md overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
            <img src={data.portrait} alt={data.name} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>{data.role}</div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl leading-tight mb-3">{data.name}</h2>
          <p className="text-sm uppercase tracking-wider text-neutral-500 mb-5">Lead Consultant · Founder · ATTP Corporate Hub</p>
          <blockquote className="border-l-4 pl-4 py-2 italic text-base leading-relaxed text-neutral-700" style={{ borderColor: b.accent }}>
            &ldquo;{data.quote}&rdquo;
          </blockquote>

          <div className="mt-6 p-5 rounded-md text-white" style={{ background: b.primary }}>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Direct contact</div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} /> {data.contact.email}</li>
              <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} /> {data.contact.phone}</li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: b.accent }} /> {data.contact.address}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Professional Biography */}
      <section>
        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Professional Biography</div>
        <p className="text-base text-neutral-700 leading-relaxed">{data.biography}</p>
      </section>

      {/* Career Highlights */}
      <section>
        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Career Highlights</div>
        <ul className="space-y-2.5">
          {data.careerHighlights.map((h) => (
            <li key={h} className="flex items-start gap-2.5 p-3 rounded-md border" style={{ borderColor: '#EEEEEE' }}>
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />
              <span className="text-sm text-neutral-700 leading-snug">{h}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Areas of expertise / methodology / philosophy etc as labelled blocks */}
      {blocks.map((bk) => {
        const Icon = bk.icon;
        return (
          <section key={bk.key}>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ background: `${b.accent}15`, color: b.accent }}>
                <Icon className="w-4.5 h-4.5" />
              </div>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: b.accent }}>{bk.label}</div>
            </div>
            <p className="text-base text-neutral-700 leading-relaxed">{data[bk.key]}</p>
          </section>
        );
      })}

      {/* Certifications */}
      <section>
        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Professional Certifications & Education</div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {data.certifications.map((c) => (
            <li key={c} className="flex items-start gap-2.5 p-3 rounded-md border" style={{ borderColor: '#EEEEEE' }}>
              <Award className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />
              <span className="text-sm text-neutral-700 leading-snug">{c}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Specialisations */}
      <section>
        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Areas of Specialisation</div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {data.specialisations.map((s) => (
            <li key={s} className="flex items-start gap-2.5 p-3 rounded-md border" style={{ borderColor: '#EEEEEE' }}>
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />
              <span className="text-sm text-neutral-700 leading-snug">{s}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Industries Served */}
      <section>
        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Industries Served</div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {data.industries.map((i) => (
            <li key={i} className="flex items-start gap-2.5 p-3 rounded-md border" style={{ borderColor: '#EEEEEE' }}>
              <Building className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />
              <span className="text-sm text-neutral-700 leading-snug">{i}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Key Accomplishments */}
      <section>
        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Key Accomplishments</div>
        <ul className="space-y-2.5">
          {data.accomplishments.map((a) => (
            <li key={a} className="flex items-start gap-2.5 p-4 rounded-md text-white" style={{ background: b.primary }}>
              <Sparkles className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: b.accent }} />
              <span className="text-sm leading-snug" style={{ color: 'rgba(255,255,255,0.9)' }}>{a}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/* ================================================================== */
/*  Countries of Operation                                             */
/* ================================================================== */
function Countries({ data, brand: b }) {
  const regions = data.countries.reduce((acc, c) => {
    acc[c.region] = acc[c.region] || [];
    acc[c.region].push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      {Object.entries(regions).map(([region, list]) => (
        <section key={region}>
          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-4" style={{ color: b.accent }}>{region}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {list.map((c) => (
              <div key={c.name} className="p-4 rounded-md border flex items-center gap-3" style={{ borderColor: '#E5E7EB' }}>
                <span className="text-3xl">{c.flag}</span>
                <div>
                  <div className="font-semibold text-sm">{c.name}</div>
                  <div className="text-[11px] text-neutral-500">{c.region}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ================================================================== */
/*  Why Choose Us                                                      */
/* ================================================================== */
function WhyChooseUs({ data, brand: b }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {data.points.map((p) => (
        <div key={p.name} className="p-6 rounded-md border" style={{ borderColor: '#E5E7EB' }}>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5" style={{ color: b.accent }} />
            <h3 className="font-display font-semibold text-lg">{p.name}</h3>
          </div>
          <p className="text-sm text-neutral-700 leading-relaxed">{p.body}</p>
        </div>
      ))}
    </div>
  );
}

/* ================================================================== */
/*  Our Approach                                                       */
/* ================================================================== */
function OurApproach({ data, brand: b }) {
  return (
    <ol className="space-y-5">
      {data.phases.map((p, i) => (
        <li key={p.name} className="p-6 rounded-md border flex gap-5" style={{ borderColor: '#E5E7EB' }}>
          <div className="text-3xl font-display font-bold flex-shrink-0" style={{ color: b.accent }}>
            {String(i + 1).padStart(2, '0')}
          </div>
          <div>
            <h3 className="font-display font-semibold text-lg mb-2">{p.name}</h3>
            <p className="text-sm text-neutral-700 leading-relaxed">{p.body}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ================================================================== */
/*  Contact Us                                                         */
/* ================================================================== */
function ContactUs({ data, brand: b }) {
  return (
    <div className="space-y-8">
      <section>
        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-4" style={{ color: b.accent }}>Contact channels</div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.channels.map((c) => (
            <li key={c.label} className="p-4 rounded-md border" style={{ borderColor: '#E5E7EB' }}>
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">{c.label}</div>
              <div className="text-base font-medium" style={{ color: b.primary }}>{c.value}</div>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-4" style={{ color: b.accent }}>Headquarters</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {data.hqs.map((hq) => (
            <div key={hq.country} className="p-4 rounded-md border" style={{ borderColor: '#E5E7EB' }}>
              <span className="text-3xl mb-2 block">{hq.flag}</span>
              <div className="font-semibold text-sm">{hq.city}</div>
              <div className="text-[11px] text-neutral-500">{hq.state}, {hq.country}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="p-6 rounded-md text-white" style={{ background: b.primary }}>
        <h3 className="font-display font-bold text-xl mb-3">Send a structured enquiry</h3>
        <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.78)' }}>
          For detailed engagement proposals and tailored consultations, use our full contact form on the main ATTP NEXUS landing page. We respond within 24 hours.
        </p>
        <Link to="/#contact" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold text-sm" style={{ background: b.accent, color: b.primary }}>
          Open Enquiry Form <ChevronRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
